/**
 * @module storage
 * @description Public storage adapter contract surface. Runtime storage
 * implementations live in provider packages; this subpath exports only the
 * interface, Zod schemas, and thin error class.
 */

import type {
  AdapterHealth as SharedAdapterHealth,
  AdapterListOptions as SharedAdapterListOptions,
  FileStorageAdapter as SharedFileStorageAdapter,
  FileStorageMetadata as SharedFileStorageMetadata,
  FileStorageMetadataValue as SharedFileStorageMetadataValue,
  FileUploadOptions as SharedFileUploadOptions,
  StorageAdapter as SharedStorageAdapter,
  StorageErrorCode as SharedStorageErrorCode,
  StorageObject as SharedStorageObject,
  StorageWriteInput as SharedStorageWriteInput,
  StoredFile as SharedStoredFile,
} from "@geenius/adapters";
import * as SharedAdapters from "@geenius/adapters";

/**
 * Health result returned by storage providers after checking object-store
 * connectivity and basic read/write availability.
 *
 * @example
 * ```ts
 * const health: AdapterHealth = await storage.getHealth();
 * ```
 */
export type AdapterHealth = SharedAdapterHealth;

/**
 * Pagination and prefix options accepted by `StorageAdapter.listObjects()`.
 *
 * @example
 * ```ts
 * const options: AdapterListOptions = { limit: 25, prefix: "avatars/" };
 * ```
 */
export type AdapterListOptions = SharedAdapterListOptions;

/**
 * JSON-compatible metadata value stored with file records.
 *
 * @example
 * ```ts
 * const value: FileStorageMetadataValue = true;
 * ```
 */
export type FileStorageMetadataValue = SharedFileStorageMetadataValue;

/**
 * Caller-controlled metadata map persisted with file records.
 *
 * @example
 * ```ts
 * const metadata: FileStorageMetadata = { owner: "u1", public: true };
 * ```
 */
export type FileStorageMetadata = SharedFileStorageMetadata;

/**
 * SDK-free file storage adapter contract implemented by local/dev helpers,
 * downstream provider packages, and app-owned storage modules.
 *
 * @example
 * ```ts
 * const file = await fileStorage.upload(blob, { name: "avatar.png" });
 * ```
 */
export type FileStorageAdapter = SharedFileStorageAdapter;

/**
 * Upload options accepted by `FileStorageAdapter.upload()`.
 *
 * @example
 * ```ts
 * const options: FileUploadOptions = { name: "avatar.png", metadata: { owner: "u1" } };
 * ```
 */
export type FileUploadOptions = SharedFileUploadOptions;

/**
 * SDK-free object/blob storage adapter contract implemented by downstream
 * provider packages and app-owned storage modules.
 *
 * @example
 * ```ts
 * const object = await storage.getObject("avatars/u1.png");
 * ```
 */
export type StorageAdapter = SharedStorageAdapter;

/**
 * Provider-level storage error codes used for file lifecycle failures.
 *
 * @example
 * ```ts
 * const code: StorageErrorCode = "NOT_FOUND";
 * ```
 */
export type StorageErrorCode = SharedStorageErrorCode;

/**
 * Serializable storage object returned by `getObject`, `putObject`, and
 * `listObjects`, including payload body, metadata, size, and update time.
 *
 * @example
 * ```ts
 * const object: StorageObject = { key: "a.txt", body: "ok", size: 2, metadata: {}, updatedAt: new Date().toISOString() };
 * ```
 */
export type StorageObject = SharedStorageObject;

/**
 * Serializable file metadata returned by file storage adapters.
 *
 * @example
 * ```ts
 * const file: StoredFile = { id: "f1", name: "a.txt", path: "/a.txt", size: 2, mimeType: "text/plain", url: "memory://a.txt", createdAt: new Date().toISOString() };
 * ```
 */
export type StoredFile = SharedStoredFile;

/**
 * Provider-neutral write payload accepted by storage adapters for object/blob
 * creation or replacement.
 *
 * @example
 * ```ts
 * const input: StorageWriteInput = { key: "a.txt", body: "ok" };
 * ```
 */
export type StorageWriteInput = SharedStorageWriteInput;

/**
 * Runtime validator for the common storage adapter health response.
 *
 * @example
 * ```ts
 * const health = AdapterHealthSchema.parse({ ok: true, checkedAt: new Date().toISOString() });
 * ```
 */
export const AdapterHealthSchema = SharedAdapters.AdapterHealthSchema;

/**
 * Runtime validator for storage list pagination and object-prefix filters.
 *
 * @example
 * ```ts
 * const options = AdapterListOptionsSchema.parse({ limit: 10, prefix: "logs/" });
 * ```
 */
export const AdapterListOptionsSchema = SharedAdapters.AdapterListOptionsSchema;

/**
 * Runtime validator for file-storage metadata scalar values.
 *
 * @example
 * ```ts
 * const value = FileStorageMetadataValueSchema.parse("invoice");
 * ```
 */
export const FileStorageMetadataValueSchema =
  SharedAdapters.FileStorageMetadataValueSchema;

/**
 * Runtime validator for caller-controlled file metadata maps.
 *
 * @example
 * ```ts
 * const metadata = FileStorageMetadataSchema.parse({ owner: "u1" });
 * ```
 */
export const FileStorageMetadataSchema =
  SharedAdapters.FileStorageMetadataSchema;

/**
 * Runtime validator for file upload options accepted by file storage adapters.
 *
 * @example
 * ```ts
 * const options = FileUploadOptionsSchema.parse({ name: "avatar.png" });
 * ```
 */
export const FileUploadOptionsSchema = SharedAdapters.FileUploadOptionsSchema;

/**
 * Contract-layer error for invalid storage payloads or provider-boundary
 * violations before a concrete SDK receives the request.
 *
 * @example
 * ```ts
 * throw new StorageContractError("Invalid storage object");
 * ```
 */
export const StorageContractError = SharedAdapters.StorageContractError;

/**
 * Provider-level storage error for file lifecycle and quota failures.
 *
 * @example
 * ```ts
 * throw new StorageError("File not found", "NOT_FOUND");
 * ```
 */
export const StorageError = SharedAdapters.StorageError;

/**
 * Runtime validator for serialized storage objects returned by providers.
 *
 * @example
 * ```ts
 * const object = StorageObjectSchema.parse({ key: "a.txt", body: "ok", size: 2, updatedAt: new Date().toISOString() });
 * ```
 */
export const StorageObjectSchema = SharedAdapters.StorageObjectSchema;

/**
 * Runtime validator for provider-neutral object/blob write payloads.
 *
 * @example
 * ```ts
 * const input = StorageWriteInputSchema.parse({ key: "a.txt", body: "ok" });
 * ```
 */
export const StorageWriteInputSchema = SharedAdapters.StorageWriteInputSchema;

/**
 * Runtime validator for serialized file records returned by file storage
 * adapters.
 *
 * @example
 * ```ts
 * const file = StoredFileSchema.parse({ id: "f1", name: "a.txt", path: "/a.txt", size: 2, mimeType: "text/plain", url: "memory://a.txt", createdAt: new Date().toISOString() });
 * ```
 */
export const StoredFileSchema = SharedAdapters.StoredFileSchema;
